import { apiClient } from './client'

export interface ApplicationPublicSurface {
  version?: string
  content_hash?: string
  capabilities?: Array<{ agent_id: string; capability: string; version: string }>
  workflows?: Array<{ agent_id: string; workflow_id: string; version: string }>
  miniapps?: Array<{ agent_id: string; miniapp_id: string; version: string }>
}

export interface ApplicationEntrypoints {
  chat?: { agent_id: string }
  sdk?: boolean
  viewer?: boolean
  workflow_api?: boolean
}

export interface ApplicationAgentMember {
  id: string
  application_id: string
  agent_id: string
  status: string
}

export interface ApplicationLLMConfig {
  run_ref?: string
}

export interface ApplicationMCPGrants {
  tools?: string[]
}

export interface Application {
  id: string
  schema_version: string
  organization_id: string
  key: string
  kind: string
  display_name?: string
  description?: string
  status: string
  auth_connector_deployment_id?: string
  manifest?: Record<string, unknown>
  modules?: unknown[]
  agents?: ApplicationAgentMember[]
  public_surface?: ApplicationPublicSurface | null
  public_surface_draft?: ApplicationPublicSurface | null
  entrypoints?: ApplicationEntrypoints | null
  llm?: ApplicationLLMConfig | null
  mcp_grants?: ApplicationMCPGrants | null
  people?: ApplicationPerson[]
  /** Who may enter (AGENT-848, AGENT-863); absent means listed. */
  people_audience?: ApplicationPeopleAudience | string
  created_at: string
  updated_at: string
}

export interface ApplicationPerson {
  user_id: string
}

/**
 * People audience of an Application (AGENT-863): the listed people only, or
 * every member of the organization on top of them.
 */
export const APPLICATION_PEOPLE_AUDIENCES = ['listed', 'organization_members'] as const
export type ApplicationPeopleAudience = (typeof APPLICATION_PEOPLE_AUDIENCES)[number]

/** The audience an Application applies: an absent or unknown value reads as listed. */
export function applicationPeopleAudience(application: Pick<Application, 'people_audience'>): ApplicationPeopleAudience {
  return application.people_audience === 'organization_members' ? 'organization_members' : 'listed'
}

export interface ApplicationPeopleAudienceResult {
  application_id: string
  people_audience: ApplicationPeopleAudience
  /** False when the Application already had this audience (nothing written). */
  changed: boolean
}

/** A person of the Application who does not reach a Run discussion yet. */
export interface RunSessionInvitee {
  user_id: string
  name: string
  email?: string
  avatar?: string
}

export interface RunSessionInvitation {
  session_id: string
  space_id: string
  user_id: string
  /** True when the invitee already reached the discussion: nothing changed. */
  already_member: boolean
  /**
   * `session`: invited into this discussion only (AGENT-862); `space`: a
   * member of its space, who reaches every discussion of the space.
   */
  access?: 'session' | 'space'
}

/** A person invited into a Run discussion (AGENT-862). */
export interface RunSessionParticipant {
  user_id: string
  name: string
  email?: string
  avatar?: string
  invited_by: string
  invited_at: string
  /** The caller may remove them: author of the invitation, owner of the space, or themselves. */
  can_remove: boolean
}

export interface RunSessionParticipantRemoval {
  session_id: string
  user_id: string
  /** False when the person was not a participant (idempotent). */
  removed: boolean
}

export interface ApplicationAPIKeyResponse {
  id: string
  name: string
  description?: string
  partial_key: string
  application_id?: string
  scopes?: string[]
  expires_at?: string | null
  last_used_at?: string | null
  created_at: string
}

function orgAppsPath(organizationId: string) {
  return `/organization/${organizationId}/applications`
}

export const applicationsAPI = {
  list: (organizationId: string) =>
    apiClient.get<Application[]>(orgAppsPath(organizationId)),

  get: (organizationId: string, applicationId: string) =>
    apiClient.get<Application>(`${orgAppsPath(organizationId)}/${applicationId}`),

  create: (
    organizationId: string,
    body: { key: string; kind?: string; display_name?: string; description?: string },
  ) => apiClient.post<Application>(orgAppsPath(organizationId), body),

  delete: (organizationId: string, applicationId: string) =>
    apiClient.delete(`${orgAppsPath(organizationId)}/${applicationId}`),

  listAgentMembers: (organizationId: string, applicationId: string) =>
    apiClient.get<ApplicationAgentMember[]>(
      `${orgAppsPath(organizationId)}/${applicationId}/members/agents`,
    ),

  addAgentMember: (organizationId: string, applicationId: string, agentId: string) =>
    apiClient.post<ApplicationAgentMember>(
      `${orgAppsPath(organizationId)}/${applicationId}/members/agents`,
      { agent_id: agentId },
    ),

  removeAgentMember: (organizationId: string, applicationId: string, agentId: string) =>
    apiClient.delete(`${orgAppsPath(organizationId)}/${applicationId}/members/agents/${agentId}`),

  getSurface: (organizationId: string, applicationId: string) =>
    apiClient.get<{
      public_surface: ApplicationPublicSurface | null
      public_surface_draft: ApplicationPublicSurface | null
      entrypoints: ApplicationEntrypoints | null
    }>(`${orgAppsPath(organizationId)}/${applicationId}/surface`),

  setSurface: (
    organizationId: string,
    applicationId: string,
    body: { public_surface: ApplicationPublicSurface; entrypoints?: ApplicationEntrypoints },
  ) =>
    apiClient.put<ApplicationPublicSurface>(
      `${orgAppsPath(organizationId)}/${applicationId}/surface`,
      body,
    ),

  publishSurface: (
    organizationId: string,
    applicationId: string,
    bump: 'major' | 'minor' | 'patch' = 'minor',
  ) =>
    apiClient.post<ApplicationPublicSurface>(
      `${orgAppsPath(organizationId)}/${applicationId}/surface/publish`,
      { bump },
    ),

  setRunConfig: (
    organizationId: string,
    applicationId: string,
    body: {
      llm?: ApplicationLLMConfig
      mcp_grants?: ApplicationMCPGrants
      clear_llm?: boolean
      clear_mcp_grants?: boolean
    },
  ) =>
    apiClient.put<{ llm: ApplicationLLMConfig | null; mcp_grants: ApplicationMCPGrants | null }>(
      `${orgAppsPath(organizationId)}/${applicationId}/run-config`,
      body,
    ),

  /**
   * Apply Application Run LLM/MCP policy onto the chat entrypoint Agent runtime
   * and bind `sessionId` (a Run session of the caller, served by the concierge)
   * to the Application. Refusals: `run_session_not_found` (404),
   * `run_session_not_bindable` (403).
   */
  applyRunPolicy: (organizationId: string, applicationId: string, sessionId?: string) =>
    apiClient.post<{
      application_key: string
      agent_id: string
      /** Session bound to the Application by this call (empty without session_id). */
      session_id?: string
      session_bound?: boolean
      llm: {
        run_ref?: string
        resolution_order?: string[]
        /** In-process LLM client only; a separate LLM server reads the bound session. */
        policy_applied?: boolean
      }
      mcp_grants?: ApplicationMCPGrants | null
    }>(`${orgAppsPath(organizationId)}/${applicationId}/run-policy/apply`, {
      session_id: sessionId,
    }),

  /**
   * People of the Application who can be invited to the Run discussion
   * `sessionId` (not yet in its space). The caller must take part in it
   * (AGENT-854). A 403/404 is a business refusal, never a lost session.
   */
  listRunSessionInvitees: (organizationId: string, applicationId: string, sessionId: string) =>
    apiClient.get<RunSessionInvitee[]>(
      `${orgAppsPath(organizationId)}/${applicationId}/run-sessions/${sessionId}/invitees`,
      { expectAuthRefusal: true },
    ),

  /**
   * Invites a person of the Application into the Run discussion, this one
   * only (AGENT-862). Idempotent (`already_member`). Refusals: `run_invitee_not_application_person`
   * (403), `run_session_not_found` (404), `run_session_not_invitable` (403),
   * `application_access_denied` (403).
   */
  inviteToRunSession: (
    organizationId: string,
    applicationId: string,
    sessionId: string,
    userId: string,
  ) =>
    apiClient.post<RunSessionInvitation>(
      `${orgAppsPath(organizationId)}/${applicationId}/run-sessions/${sessionId}/invitations`,
      { user_id: userId },
      { expectAuthRefusal: true },
    ),

  /**
   * People invited into the Run discussion (AGENT-862), each with
   * `can_remove` for the caller. Same refusals as the invitees.
   */
  listRunSessionParticipants: (organizationId: string, applicationId: string, sessionId: string) =>
    apiClient.get<RunSessionParticipant[]>(
      `${orgAppsPath(organizationId)}/${applicationId}/run-sessions/${sessionId}/participants`,
      { expectAuthRefusal: true },
    ),

  /**
   * Removes a person invited into the Run discussion; their channel closes at
   * the next recheck. Idempotent (`removed: false`). Refusal:
   * `run_participant_removal_forbidden` (403).
   */
  removeRunSessionParticipant: (
    organizationId: string,
    applicationId: string,
    sessionId: string,
    userId: string,
  ) =>
    apiClient.delete<RunSessionParticipantRemoval>(
      `${orgAppsPath(organizationId)}/${applicationId}/run-sessions/${sessionId}/participants/${encodeURIComponent(userId)}`,
      { expectAuthRefusal: true },
    ),

  listAPIKeys: (organizationId: string, applicationId: string) =>
    apiClient.get<ApplicationAPIKeyResponse[]>(
      `${orgAppsPath(organizationId)}/${applicationId}/apikeys`,
    ),

  createAPIKey: (
    organizationId: string,
    applicationId: string,
    body: {
      name: string
      description?: string
      scopes?: string[]
      expires_at?: string | null
    },
  ) =>
    apiClient.post<{ api_key: ApplicationAPIKeyResponse; plaintext_key: string }>(
      `${orgAppsPath(organizationId)}/${applicationId}/apikeys`,
      body,
    ),

  deleteAPIKey: (organizationId: string, applicationId: string, apiKeyId: string) =>
    apiClient.delete(`${orgAppsPath(organizationId)}/${applicationId}/apikeys/${apiKeyId}`),

  listPeople: (organizationId: string, applicationId: string) =>
    apiClient.get<ApplicationPerson[]>(
      `${orgAppsPath(organizationId)}/${applicationId}/members/people`,
    ),

  addPerson: (organizationId: string, applicationId: string, userId: string) =>
    apiClient.post<ApplicationPerson>(
      `${orgAppsPath(organizationId)}/${applicationId}/members/people`,
      { user_id: userId },
    ),

  removePerson: (organizationId: string, applicationId: string, userId: string) =>
    apiClient.delete(
      `${orgAppsPath(organizationId)}/${applicationId}/members/people/${userId}`,
    ),

  /** Organization administrators only, like the people routes (AGENT-863). */
  setPeopleAudience: (organizationId: string, applicationId: string, audience: ApplicationPeopleAudience) =>
    apiClient.put<ApplicationPeopleAudienceResult>(
      `${orgAppsPath(organizationId)}/${applicationId}/members/people/audience`,
      { people_audience: audience },
    ),
}
